import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Bot, Sparkles, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

const SESSION_KEY = 'edu_chat_session';

function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = 'sess_' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export default function ChatWidget() {
  const [enabled, setEnabled] = useState(false);
  const [welcome, setWelcome] = useState('');
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef();
  const sessionId = useRef(getSession());

  useEffect(() => {
    api.llmConfig().then(cfg => {
      setEnabled(!!cfg?.enabled);
      setWelcome(cfg?.welcomeMessage || "Hi! I'm your course assistant, your Skillbridge.guru assistant. Ask me about any course.");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && msgs.length === 0 && welcome) {
      setMsgs([{ role: 'assistant', content: welcome }]);
    }
  }, [open, welcome, msgs.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMsgs(m => [...m, { role: 'user', content: text }]);
    setBusy(true);
    try {
      const res = await api.chatSend({ session_id: sessionId.current, message: text });
      if (res.session_id) sessionId.current = res.session_id;
      setMsgs(m => [...m, { role: 'assistant', content: res.reply || 'Sorry, I could not reply.' }]);
    } catch (e) {
      setMsgs(m => [...m, { role: 'assistant', content: '⚠️ ' + (e.response?.data?.detail || 'Chat error — please try again.') }]);
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-[#6b47dc] via-[#a855f7] to-[#ff5722] grid place-items-center text-white shadow-2xl hover:scale-110 transition-transform pulse-glow group"
          aria-label="Open chat"
        >
          <MessageCircle size={26} />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />
          <span className="absolute right-full mr-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg pointer-events-none">
            Ask us — AI assistant
          </span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-purple-100 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-gradient-to-r from-[#6b47dc] via-[#7c3aed] to-[#ff5722] p-4 text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 grid place-items-center backdrop-blur">
              <Bot size={20} />
            </div>
            <div className="flex-1">
              <div className="font-bold">Eddie</div>
              <div className="text-xs opacity-90 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Online • AI assistant</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-full"><X size={18} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-purple-50/40 to-white">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#6b47dc] to-[#ff5722] grid place-items-center text-white flex-shrink-0 mr-2 mt-0.5">
                    <Sparkles size={13} />
                  </div>
                )}
                <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                  m.role === 'user'
                    ? 'bg-[#6b47dc] text-white rounded-br-md'
                    : 'bg-white border border-gray-100 text-gray-800 rounded-bl-md shadow-sm'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#6b47dc] to-[#ff5722] grid place-items-center text-white flex-shrink-0 mr-2">
                  <Sparkles size={13} />
                </div>
                <div className="px-4 py-3 rounded-2xl bg-white border border-gray-100 shadow-sm flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#6b47dc] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#a855f7] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#ff5722] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-100 bg-white">
            <div className="flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about any course..."
                rows={1}
                className="flex-1 resize-none px-3 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 text-sm max-h-24"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6b47dc] to-[#ff5722] grid place-items-center text-white disabled:opacity-40 hover:scale-105 transition-transform"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <div className="text-[10px] text-gray-400 text-center mt-1.5">Powered by AI • Ask about courses, prices, careers</div>
          </div>
        </div>
      )}
    </>
  );
}
